import { Button, Slider } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import {
  EditorSettingService,
  formatKeybinding,
  keybindingFromEvent,
} from '@affine/core/modules/editor-setting';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import type { ShortcutsInfo } from '../../../../../components/hooks/affine/use-shortcuts';
import {
  useEdgelessShortcuts,
  useGeneralShortcuts,
  useMarkdownShortcuts,
  usePageShortcuts,
} from '../../../../../components/hooks/affine/use-shortcuts';
import {
  fontSizeContainer,
  fontSizeSlider,
  fontSizeValue,
} from '../editor/style.css';
import { shortcutKey, shortcutKeyContainer, shortcutRow } from './style.css';

const ShortcutsPanel = ({
  shortcutsInfo,
}: {
  shortcutsInfo: ShortcutsInfo;
}) => {
  return (
    <SettingWrapper title={shortcutsInfo.title}>
      {Object.entries(shortcutsInfo.shortcuts).map(([title, shortcuts]) => {
        return (
          <div key={title} className={shortcutRow}>
            <span>{title}</span>
            <div className={shortcutKeyContainer}>
              {shortcuts.map(key => {
                return (
                  <span className={shortcutKey} key={key}>
                    {key}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </SettingWrapper>
  );
};

const PrivateWorkspacesShortcut = () => {
  const t = useI18n();
  const editorSetting = useService(EditorSettingService).editorSetting;
  const setting = editorSetting.privateWorkspacesShortcut;
  const binding = useLiveData(setting.$);
  const longPressSeconds = useLiveData(
    editorSetting.privateWorkspacesLongPressSeconds.$
  );
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    if (!recording) return;
    // capture on window so Escape doesn't close the dialog and the old
    // binding doesn't fire while recording
    const onKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setRecording(false);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        setting.set('');
        setRecording(false);
      } else {
        const next = keybindingFromEvent(e, environment.isMacOs);
        if (!next) return;
        setting.set(next);
        setRecording(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [recording, setting]);

  return (
    <SettingWrapper title={t['com.affine.private-workspaces.title']()}>
      <SettingRow
        name={t['com.affine.private-workspaces.shortcut']()}
        desc={t['com.affine.private-workspaces.shortcut-hint']()}
      >
        <Button
          onClick={() => setRecording(r => !r)}
          onBlur={() => setRecording(false)}
          data-testid="private-workspaces-shortcut"
        >
          {recording ? (
            t['com.affine.private-workspaces.shortcut-recording']()
          ) : binding ? (
            <span className={shortcutKeyContainer}>
              {formatKeybinding(binding, environment.isMacOs).map(key => (
                <span className={shortcutKey} key={key}>
                  {key}
                </span>
              ))}
            </span>
          ) : (
            t['com.affine.private-workspaces.shortcut-none']()
          )}
        </Button>
      </SettingRow>
      <SettingRow
        name={t['com.affine.private-workspaces.long-press']()}
        desc={t['com.affine.private-workspaces.long-press-hint']()}
      >
        <div className={fontSizeContainer}>
          <Slider
            value={[longPressSeconds]}
            onValueChange={([value]) =>
              editorSetting.privateWorkspacesLongPressSeconds.set(value)
            }
            min={0.5}
            max={10}
            step={0.5}
            className={fontSizeSlider}
          />
          <span className={fontSizeValue}>{longPressSeconds}s</span>
        </div>
      </SettingRow>
    </SettingWrapper>
  );
};

export const Shortcuts = () => {
  const t = useI18n();

  const markdownShortcutsInfo = useMarkdownShortcuts();
  const pageShortcutsInfo = usePageShortcuts();
  const edgelessShortcutsInfo = useEdgelessShortcuts();
  const generalShortcutsInfo = useGeneralShortcuts();

  return (
    <>
      <SettingHeader
        title={t['com.affine.keyboardShortcuts.title']()}
        subtitle={t['com.affine.keyboardShortcuts.subtitle']()}
        data-testid="keyboard-shortcuts-title"
      />
      <PrivateWorkspacesShortcut />
      <ShortcutsPanel shortcutsInfo={generalShortcutsInfo} />
      <ShortcutsPanel shortcutsInfo={pageShortcutsInfo} />
      <ShortcutsPanel shortcutsInfo={edgelessShortcutsInfo} />
      <ShortcutsPanel shortcutsInfo={markdownShortcutsInfo} />
    </>
  );
};
